/**
 * UTM — OPNsense slovesa show/set/discard/commit/compare/run/backup nad
 * nat, rule, alias, route, interface. Widget volá tytéž MCP tooly.
 */
import { type ReactNode, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Cable,
  GitCompare,
  Plus,
  Route,
  Server,
  Shield,
  Signpost,
  User,
  Waypoints,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/Textarea';
import { callWidgetTool } from './scope';
import type { EffectiveScope, WidgetRenderProps, WidgetSpec } from './types';
import { useTranslation } from 'react-i18next';
import i18n from '../lib/i18n';
import { Fail2banReactionCard } from '../components/ui/Fail2banReactionToggle';

type UtmKind = 'alias' | 'rule' | 'nat' | 'route' | 'instance';

type UtmObj = {
  kind: UtmKind;
  name: string;
  path?: string;
  spec: Record<string, unknown>;
  elements?: string[];
  live_count?: number;
};

function specStr(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function asBool(v: unknown, fallback = true): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function dash(v: unknown): string {
  const s = specStr(v);
  return s || 'any';
}

function useUtmQuery<T>(
  scope: EffectiveScope,
  tool: string,
  args: Record<string, unknown>,
  key: unknown[],
) {
  return useQuery({
    queryKey: ['utm', scope.node ?? 'local', ...key],
    queryFn: () => callWidgetTool<T>(tool, args, scope, { silent: true }),
    refetchInterval: 15_000,
  });
}

function invalidateUtm(qc: ReturnType<typeof useQueryClient>, scope: EffectiveScope) {
  void qc.invalidateQueries({ queryKey: ['utm', scope.node ?? 'local'] });
}

type Draft = {
  name: string;
  backend: string;
  action: string;
  chain: string;
  proto: string;
  iifname: string;
  oifname: string;
  saddr: string;
  daddr: string;
  dport: string;
  sport: string;
  to: string;
  family: string;
  elements: string;
  dest: string;
  via: string;
  dev: string;
  hostname: string;
  node_id: string;
  description: string;
  enabled: boolean;
  log: boolean;
};

function emptyDraft(kind: UtmKind): Draft {
  return {
    name: kind === 'instance' ? 'local' : '',
    backend: 'nft',
    action: kind === 'nat'
      ? 'dnat'
      : kind === 'rule'
      ? 'accept'
      : kind === 'instance'
      ? 'native'
      : '',
    chain: 'forward',
    proto: kind === 'nat' ? 'udp' : 'any',
    iifname: '',
    oifname: '',
    saddr: '',
    daddr: '',
    dport: kind === 'nat' ? '53' : '',
    sport: '',
    to: '',
    family: 'ipv4',
    elements: '',
    dest: '',
    via: '',
    dev: '',
    hostname: '',
    node_id: '',
    description: '',
    enabled: true,
    log: false,
  };
}

function draftFrom(o: UtmObj): Draft {
  const s = o.spec ?? {};
  return {
    name: o.name,
    backend: specStr(s.backend) || 'nft',
    action: specStr(s.action),
    chain: specStr(s.chain) || 'forward',
    proto: specStr(s.proto) || (o.kind === 'nat' ? 'udp' : 'any'),
    iifname: specStr(s.iifname),
    oifname: specStr(s.oifname),
    saddr: specStr(s.saddr),
    daddr: specStr(s.daddr),
    dport: s.dport != null ? String(s.dport) : '',
    sport: s.sport != null ? String(s.sport) : '',
    to: specStr(s.to),
    family: specStr(s.family) || 'ipv4',
    elements: Array.isArray(s.elements) ? (s.elements as string[]).join('\n') : specStr(s.elements),
    dest: specStr(s.dest),
    via: specStr(s.via),
    dev: specStr(s.dev),
    hostname: specStr(s.hostname),
    node_id: specStr(s.node_id),
    description: specStr(s.description),
    enabled: asBool(s.enabled, true),
    log: asBool(s.log, false),
  };
}

function specFromDraft(kind: UtmKind, d: Draft): Record<string, unknown> {
  const numOrUndef = (x: string) => {
    const n = Number(x);
    return x.trim() && Number.isFinite(n) ? n : undefined;
  };
  const opt = (x: string) => (x.trim() ? x.trim() : undefined);
  const spec: Record<string, unknown> = {
    enabled: d.enabled,
  };
  if (d.description.trim()) spec.description = d.description.trim();
  if (kind === 'alias') {
    spec.family = d.family;
    spec.elements = d.elements.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
    return spec;
  }
  if (kind === 'route') {
    spec.dest = d.dest.trim();
    if (opt(d.via)) spec.via = d.via.trim();
    if (opt(d.dev)) spec.dev = d.dev.trim();
    return spec;
  }
  if (kind === 'instance') {
    spec.backend = d.action || 'native';
    if (opt(d.hostname)) spec.hostname = d.hostname.trim();
    if (opt(d.node_id)) spec.node_id = d.node_id.trim();
    return spec;
  }
  if (kind === 'nat') {
    spec.action = d.action;
    if (d.action === 'dnat') {
      spec.proto = d.proto === 'tcp' ? 'tcp' : 'udp';
      spec.dport = numOrUndef(d.dport);
      spec.to = d.to.trim();
    }
    if (opt(d.iifname)) spec.iifname = d.iifname.trim();
    if (opt(d.oifname)) spec.oifname = d.oifname.trim();
    if (opt(d.saddr)) spec.saddr = d.saddr.trim();
    if (opt(d.daddr)) spec.daddr = d.daddr.trim();
    return spec;
  }
  spec.action = d.action;
  spec.chain = d.chain;
  spec.backend = d.backend;
  if (d.proto && d.proto !== 'any') spec.proto = d.proto;
  if (opt(d.iifname)) spec.iifname = d.iifname.trim();
  if (opt(d.oifname)) spec.oifname = d.oifname.trim();
  if (opt(d.saddr)) spec.saddr = d.saddr.trim();
  if (opt(d.daddr)) spec.daddr = d.daddr.trim();
  const dport = numOrUndef(d.dport);
  const sport = numOrUndef(d.sport);
  if (dport != null) spec.dport = dport;
  if (sport != null) spec.sport = sport;
  if (d.log) spec.log = true;
  return spec;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function ObjectForm({
  kind,
  draft,
  setDraft,
  nameLocked,
}: {
  kind: UtmKind;
  draft: Draft;
  setDraft: (d: Draft) => void;
  nameLocked: boolean;
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
      <Field label='path (jméno)'>
        <Input
          size='sm'
          className='font-mono'
          value={draft.name}
          disabled={nameLocked}
          onChange={(e) => set({ name: e.target.value })}
          placeholder={`${kind}/jmeno`}
        />
      </Field>
      <Field label='enabled'>
        <Select
          size='sm'
          value={draft.enabled ? '1' : '0'}
          options={[
            { value: '1', label: 'yes' },
            { value: '0', label: 'no' },
          ]}
          onChange={(e) => set({ enabled: e.target.value === '1' })}
        />
      </Field>
      {kind === 'alias' && (
        <>
          <Field label='family'>
            <Select
              size='sm'
              value={draft.family}
              options={['ipv4', 'ipv6']}
              onChange={(e) => set({ family: e.target.value })}
            />
          </Field>
          <div className='sm:col-span-2'>
            <Field label={t('registry.utm.elementsRadekNeboCarka', 'elements (řádek nebo čárka)')}>
              <Textarea
                size='sm'
                className='font-mono'
                value={draft.elements}
                onChange={(e) => set({ elements: e.target.value })}
                placeholder='1.2.3.4&#10;10.0.0.0/8'
              />
            </Field>
          </div>
        </>
      )}
      {kind === 'route' && (
        <>
          <Field label='dest'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.dest}
              onChange={(e) => set({ dest: e.target.value })}
              placeholder='10.119.0.0/16'
            />
          </Field>
          <Field label='via'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.via}
              onChange={(e) => set({ via: e.target.value })}
            />
          </Field>
          <Field label='dev'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.dev}
              onChange={(e) => set({ dev: e.target.value })}
            />
          </Field>
        </>
      )}
      {kind === 'instance' && (
        <>
          <Field label='backend'>
            <Select
              size='sm'
              value={draft.action || 'native'}
              options={['native', 'opnsense', 'proxmox', 'unifi']}
              onChange={(e) => set({ action: e.target.value })}
            />
          </Field>
          <Field label='hostname'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.hostname}
              onChange={(e) => set({ hostname: e.target.value })}
            />
          </Field>
          <Field label='node_id'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.node_id}
              onChange={(e) => set({ node_id: e.target.value })}
            />
          </Field>
        </>
      )}
      {kind === 'nat' && (
        <>
          <Field label='action'>
            <Select
              size='sm'
              value={draft.action}
              options={[
                { value: 'dnat', label: 'dnat (port forward)' },
                { value: 'masquerade', label: 'masquerade (outbound)' },
              ]}
              onChange={(e) => set({ action: e.target.value })}
            />
          </Field>
          {draft.action === 'dnat' && (
            <>
              <Field label='proto'>
                <Select
                  size='sm'
                  value={draft.proto}
                  options={['udp', 'tcp']}
                  onChange={(e) => set({ proto: e.target.value })}
                />
              </Field>
              <Field label='dest port'>
                <Input
                  size='sm'
                  className='font-mono'
                  value={draft.dport}
                  onChange={(e) => set({ dport: e.target.value })}
                />
              </Field>
              <Field label='redirect to (ip:port)'>
                <Input
                  size='sm'
                  className='font-mono'
                  value={draft.to}
                  onChange={(e) => set({ to: e.target.value })}
                  placeholder='172.17.0.1:53'
                />
              </Field>
            </>
          )}
          <Field label='interface in'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.iifname}
              onChange={(e) => set({ iifname: e.target.value })}
              placeholder='docker0'
            />
          </Field>
          <Field label='interface out'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.oifname}
              onChange={(e) => set({ oifname: e.target.value })}
              placeholder='ens18'
            />
          </Field>
          <Field label='source'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.saddr}
              onChange={(e) => set({ saddr: e.target.value })}
            />
          </Field>
          <Field label='destination'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.daddr}
              onChange={(e) => set({ daddr: e.target.value })}
            />
          </Field>
        </>
      )}
      {kind === 'rule' && (
        <>
          <Field label='backend'>
            <Select
              size='sm'
              value={draft.backend}
              options={[
                { value: 'nft', label: 'nft (inet acm)' },
                { value: 'docker-user', label: 'Docker policy (DOCKER-USER)' },
              ]}
              onChange={(e) => set({ backend: e.target.value })}
            />
          </Field>
          <Field label='action'>
            <Select
              size='sm'
              value={draft.action}
              options={[
                { value: 'accept', label: 'pass' },
                { value: 'drop', label: 'block' },
              ]}
              onChange={(e) => set({ action: e.target.value })}
            />
          </Field>
          <Field label='chain'>
            <Select
              size='sm'
              value={draft.chain}
              options={['forward', 'input']}
              onChange={(e) => set({ chain: e.target.value })}
            />
          </Field>
          <Field label='proto'>
            <Select
              size='sm'
              value={draft.proto}
              options={['any', 'tcp', 'udp']}
              onChange={(e) => set({ proto: e.target.value })}
            />
          </Field>
          <Field label='interface in'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.iifname}
              onChange={(e) => set({ iifname: e.target.value })}
            />
          </Field>
          <Field label='interface out'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.oifname}
              onChange={(e) => set({ oifname: e.target.value })}
            />
          </Field>
          <Field label='source'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.saddr}
              onChange={(e) => set({ saddr: e.target.value })}
            />
          </Field>
          <Field label='destination'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.daddr}
              onChange={(e) => set({ daddr: e.target.value })}
            />
          </Field>
          <Field label='source port'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.sport}
              onChange={(e) => set({ sport: e.target.value })}
            />
          </Field>
          <Field label='dest port'>
            <Input
              size='sm'
              className='font-mono'
              value={draft.dport}
              onChange={(e) => set({ dport: e.target.value })}
            />
          </Field>
          <Field label='log'>
            <Select
              size='sm'
              value={draft.log ? '1' : '0'}
              options={[
                { value: '0', label: 'no' },
                { value: '1', label: 'yes' },
              ]}
              onChange={(e) => set({ log: e.target.value === '1' })}
            />
          </Field>
        </>
      )}
      <div className='sm:col-span-2'>
        <Field label='description'>
          <Input
            size='sm'
            value={draft.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </Field>
      </div>
    </div>
  );
}

function UtmKindEditor({ kind, scope }: { kind: UtmKind; scope: EffectiveScope }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useUtmQuery<{ ok?: boolean; objects?: UtmObj[]; error?: string }>(
    scope,
    'utm.show',
    { path: kind },
    ['show', kind],
  );
  const objects = q.data?.objects ?? [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [pendingDiscard, setPendingDiscard] = useState<string | null>(null);
  const nameLocked = editing != null;

  const setMut = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('prázdný formulář');
      const name = draft.name.trim();
      const spec = specFromDraft(kind, draft);
      return callWidgetTool<{ ok?: boolean; error?: string; path?: string }>(
        'utm.set',
        { path: `${kind}/${name}`, spec },
        scope,
        { label: `utm.set ${kind}/${name}` },
      );
    },
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error ?? i18n.t('registry.utm.setSelhal', 'set selhal'));
        return;
      }
      toast.success(`set ${r.path ?? kind}`);
      setDraft(null);
      setEditing(null);
      invalidateUtm(qc, scope);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const discardMut = useMutation({
    mutationFn: (path: string) =>
      callWidgetTool<{ ok?: boolean; error?: string }>(
        'utm.discard',
        { path },
        scope,
        { label: `utm.discard ${path}` },
      ),
    onSuccess: (r, path) => {
      if (r?.ok === false) {
        toast.error(r.error ?? i18n.t('registry.utm.discardSelhal', 'discard selhal'));
        return;
      }
      toast.success(`discard ${path}`);
      setPendingDiscard(null);
      invalidateUtm(qc, scope);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='px-3 py-2 space-y-2'>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-xs text-gray-500 font-mono'>show {kind}</span>
        <div className='flex gap-2'>
          <Button size='sm' variant='secondary' onClick={() => void q.refetch()}>
            show
          </Button>
          <Button
            size='sm'
            onClick={() => {
              setEditing(null);
              setDraft(emptyDraft(kind));
            }}
          >
            <Plus size={14} /> set
          </Button>
        </div>
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full text-xs font-mono'>
          <thead className='text-gray-500 text-left'>
            <tr className='border-b border-gray-800'>
              <th className='py-1 pr-2 font-medium'>path</th>
              {kind === 'rule' && (
                <>
                  <th className='py-1 pr-2 font-medium'>action</th>
                  <th className='py-1 pr-2 font-medium'>proto</th>
                  <th className='py-1 pr-2 font-medium'>source</th>
                  <th className='py-1 pr-2 font-medium'>dest</th>
                  <th className='py-1 pr-2 font-medium'>port</th>
                  <th className='py-1 pr-2 font-medium'>if</th>
                </>
              )}
              {kind === 'nat' && (
                <>
                  <th className='py-1 pr-2 font-medium'>action</th>
                  <th className='py-1 pr-2 font-medium'>proto</th>
                  <th className='py-1 pr-2 font-medium'>if</th>
                  <th className='py-1 pr-2 font-medium'>port</th>
                  <th className='py-1 pr-2 font-medium'>to</th>
                </>
              )}
              {kind === 'alias' && (
                <>
                  <th className='py-1 pr-2 font-medium'>family</th>
                  <th className='py-1 pr-2 font-medium'>elements</th>
                </>
              )}
              {kind === 'route' && (
                <>
                  <th className='py-1 pr-2 font-medium'>dest</th>
                  <th className='py-1 pr-2 font-medium'>via</th>
                  <th className='py-1 pr-2 font-medium'>dev</th>
                </>
              )}
              {kind === 'instance' && (
                <>
                  <th className='py-1 pr-2 font-medium'>backend</th>
                  <th className='py-1 pr-2 font-medium'>hostname</th>
                  <th className='py-1 pr-2 font-medium'>node</th>
                </>
              )}
              <th className='py-1 pr-2 font-medium'>on</th>
              <th className='py-1 font-medium' />
            </tr>
          </thead>
          <tbody>
            {objects.length === 0 && (
              <tr>
                <td colSpan={8} className='py-3 text-gray-600'>
                  {t('registry.utm.zadny', 'žádný')} {kind} {t('registry.utm.setZapiseDoKatalogu', '— set zapíše do katalogu, commit až na Compare')}
                </td>
              </tr>
            )}
            {objects.map((o) => {
              const path = o.path ?? `${o.kind}/${o.name}`;
              const s = o.spec ?? {};
              return (
                <tr key={path} className='border-b border-gray-800/60 text-gray-200'>
                  <td className='py-1.5 pr-2'>{path}</td>
                  {kind === 'rule' && (
                    <>
                      <td className='pr-2'>
                        <Badge variant={s.action === 'drop' ? 'error' : 'success'}>
                          {s.action === 'drop' ? 'block' : 'pass'}
                        </Badge>
                      </td>
                      <td className='pr-2'>{dash(s.proto)}</td>
                      <td className='pr-2'>{dash(s.saddr)}</td>
                      <td className='pr-2'>{dash(s.daddr)}</td>
                      <td className='pr-2'>{dash(s.dport)}</td>
                      <td className='pr-2'>{dash(s.iifname || s.oifname)}</td>
                    </>
                  )}
                  {kind === 'nat' && (
                    <>
                      <td className='pr-2'>{dash(s.action)}</td>
                      <td className='pr-2'>{dash(s.proto)}</td>
                      <td className='pr-2'>{dash(s.iifname || s.oifname)}</td>
                      <td className='pr-2'>{dash(s.dport)}</td>
                      <td className='pr-2'>{dash(s.to)}</td>
                    </>
                  )}
                  {kind === 'alias' && (
                    <>
                      <td className='pr-2'>
                        {dash(s.family)}
                        {s.source === 'ip_lists' ? ' · ip_lists' : ''}
                      </td>
                      <td className='pr-2 truncate max-w-[16rem]'>
                        {dash(o.elements ?? s.elements)}
                        {o.live_count != null ? ` (${o.live_count})` : ''}
                      </td>
                    </>
                  )}
                  {kind === 'route' && (
                    <>
                      <td className='pr-2'>{dash(s.dest)}</td>
                      <td className='pr-2'>{dash(s.via)}</td>
                      <td className='pr-2'>{dash(s.dev)}</td>
                    </>
                  )}
                  {kind === 'instance' && (
                    <>
                      <td className='pr-2'>{dash(s.backend)}</td>
                      <td className='pr-2'>{dash(s.hostname)}</td>
                      <td className='pr-2'>{dash(s.node_id)}</td>
                    </>
                  )}
                  <td className='pr-2'>{asBool(s.enabled, true) ? 'yes' : 'no'}</td>
                  <td className='py-1.5 text-right whitespace-nowrap'>
                    <Button
                      size='sm'
                      variant='ghost'
                      onClick={() => {
                        setEditing(o.name);
                        setDraft(draftFrom(o));
                      }}
                    >
                      set
                    </Button>
                    <Button size='sm' variant='ghost' onClick={() => setPendingDiscard(path)}>
                      discard
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Modal
        open={!!draft}
        onClose={() => {
          setDraft(null);
          setEditing(null);
        }}
        title={`utm.set ${kind}${draft?.name ? `/${draft.name}` : ''}`}
        size='lg'
        footer={
          <div className='flex justify-end gap-2'>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => {
                setDraft(null);
                setEditing(null);
              }}
            >
              {t('registry.utm.zrusit', 'zrušit')}
            </Button>
            <Button
              size='sm'
              onClick={() => setMut.mutate()}
              disabled={setMut.isPending || !draft?.name.trim()}
            >
              set
            </Button>
          </div>
        }
      >
        {draft && (
          <ObjectForm kind={kind} draft={draft} setDraft={setDraft} nameLocked={nameLocked} />
        )}
      </Modal>
      <ConfirmDialog
        open={!!pendingDiscard}
        title={`utm.discard ${pendingDiscard ?? ''}`}
        description={t('registry.utm.smazeDeklaraciKataloguZivy', 'Smaže deklaraci z katalogu. Živý nft až po commit.')}
        confirmLabel='discard'
        danger
        onConfirm={() =>
          pendingDiscard && discardMut.mutate(pendingDiscard)}
        onCancel={() =>
          setPendingDiscard(null)}
      />
    </div>
  );
}

function UtmStatusWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const compare = useUtmQuery<{
    ok?: boolean;
    clean?: boolean;
    live_present?: boolean;
    note?: string;
    error?: string;
  }>(scope, 'utm.compare', {}, ['compare']);
  const sys = useUtmQuery<{
    hostname?: string;
    backend?: string;
    nft_present?: boolean;
    objects?: number;
    counts?: Record<string, number>;
    interfaces?: number;
    error?: string;
  }>(scope, 'utm.sysinfo', {}, ['sysinfo']);
  const backups = useUtmQuery<
    { backups?: Array<{ name: string; saved_at?: string; count?: number }> }
  >(
    scope,
    'utm.backup',
    {},
    ['backup'],
  );
  const [saveName, setSaveName] = useState('');

  const commit = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok?: boolean; applied?: boolean; error?: string }>(
        'utm.commit',
        { apply: true },
        scope,
        { label: 'utm.commit' },
      ),
    onSuccess: (r) => {
      if (r?.ok && r.applied) toast.success('commit — inet acm zapsaný');
      else toast.error(r?.error ?? i18n.t('registry.utm.commitSelhal', 'commit selhal'));
      invalidateUtm(qc, scope);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const saveMut = useMutation({
    mutationFn: (name: string) =>
      callWidgetTool<{ ok?: boolean; error?: string; saved?: string }>(
        'utm.save',
        { name },
        scope,
        { label: `utm.save ${name}` },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) toast.error(r.error ?? i18n.t('registry.utm.saveSelhal', 'save selhal'));
      else toast.success(`save ${r.saved}`);
      setSaveName('');
      invalidateUtm(qc, scope);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const restoreMut = useMutation({
    mutationFn: (name: string) =>
      callWidgetTool<{ ok?: boolean; error?: string }>(
        'utm.restore',
        { name },
        scope,
        { label: `utm.restore ${name}` },
      ),
    onSuccess: (r, name) => {
      if (r?.ok === false) toast.error(r.error ?? i18n.t('registry.utm.restoreSelhal', 'restore selhal'));
      else toast.success(`restore ${name} — commit zvlášť`);
      invalidateUtm(qc, scope);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const runNft = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok?: boolean; present?: boolean; error?: string }>(
        'utm.run',
        { service: 'nft', command: 'check' },
        scope,
        { label: 'utm.run nft check' },
      ),
    onSuccess: (r) => {
      if (r?.ok) toast.success('nft check ok');
      else toast.error(r?.error ?? i18n.t('registry.utm.nftCheckSelhal', 'nft check selhal'));
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const d = compare.data;
  const clean = d?.clean === true;
  const counts = useMemo(() => {
    const c = sys.data?.counts ?? {};
    return Object.entries(c).map(([k, n]) => `${k}=${n}`).join(' ') || t('registry.utm.prazdnyKatalog', 'prázdný katalog');
  }, [sys.data?.counts]);

  return (
    <div className='px-4 py-3 space-y-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant={clean ? 'success' : 'warning'}>
          {clean ? t('registry.utm.compareCisty', 'compare čistý') : t('registry.utm.compareSeLisi', 'compare se liší')}
        </Badge>
        <Badge variant={sys.data?.nft_present ? 'success' : 'warning'}>
          nft {sys.data?.nft_present ? 'inet acm' : 'není'}
        </Badge>
        <span className='text-sm text-gray-400'>
          {d?.note ?? (compare.isLoading ? t('registry.utm.nacitam', 'načítám…') : d?.error ?? '')}
        </span>
      </div>
      <p className='text-xs text-gray-500 font-mono'>
        {sys.data?.hostname ?? '…'} · {sys.data?.backend ?? 'native'} · {counts}{' '}
        · if={sys.data?.interfaces ?? '—'}
      </p>
      <div className='flex flex-wrap gap-2'>
        <Button size='sm' variant='secondary' onClick={() => void compare.refetch()}>
          compare
        </Button>
        <Button
          size='sm'
          variant='secondary'
          onClick={() => runNft.mutate()}
          disabled={runNft.isPending}
        >
          run nft check
        </Button>
        <Button size='sm' onClick={() => commit.mutate()} disabled={commit.isPending}>
          commit
        </Button>
      </div>
      <div className='flex flex-wrap items-end gap-2'>
        <div className='min-w-[10rem]'>
          <Label>save</Label>
          <Input
            size='sm'
            className='font-mono'
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder='jmeno_zalohy'
          />
        </div>
        <Button
          size='sm'
          variant='secondary'
          disabled={!saveName.trim() || saveMut.isPending}
          onClick={() => saveMut.mutate(saveName.trim())}
        >
          save
        </Button>
      </div>
      {(backups.data?.backups ?? []).length > 0 && (
        <div className='space-y-1'>
          <span className='text-xs text-gray-500'>backup</span>
          {(backups.data?.backups ?? []).map((b) => (
            <div key={b.name} className='flex items-center justify-between gap-2 text-xs font-mono'>
              <span className='text-gray-300'>
                {b.name}
                <span className='text-gray-600'>{b.count ?? 0} obj</span>
              </span>
              <Button
                size='sm'
                variant='ghost'
                onClick={() =>
                  restoreMut.mutate(b.name)}
              >
                restore
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function kindWidget(kind: UtmKind, title: string, icon: WidgetSpec['icon']): WidgetSpec {
  const Body = ({ scope }: WidgetRenderProps) => <UtmKindEditor kind={kind} scope={scope} />;
  return {
    id: `utm.${kind === 'rule' ? 'rules' : kind === 'alias' ? 'aliases' : kind}`,
    title,
    module: 'utm',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon,
    component: Body,
  };
}

export const UTM_WIDGETS: WidgetSpec[] = [
  {
    // Jeden přepínač mostu fail2ban → self-defence; stejná komponenta je v panelu
    // publikace, na kartě služeb uzlu a v Zabezpečení acm-self (Filip 2026-10-07).
    id: 'self-defence.reaction',
    title: 'Fail2ban reaction',
    module: 'self-defence',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md'],
    since: '2.2.1',
    component: () => <Fail2banReactionCard />,
  },
  {
    id: 'utm.status',
    title: 'UTM compare / commit',
    module: 'utm',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: GitCompare,
    component: UtmStatusWidget,
  },
  kindWidget('nat', 'NAT', Route),
  kindWidget('rule', 'Rules', Shield),
  kindWidget('alias', 'Aliases', Signpost),
  kindWidget('route', 'Routes', Waypoints),
  kindWidget('instance', 'Instance', Server),
  {
    id: 'utm.net',
    title: 'Net',
    module: 'utm',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: User,
    component: function NetBody({ scope }: WidgetRenderProps) {
      const { t } = useTranslation();
      const qc = useQueryClient();
      const q = useUtmQuery<{
        objects?: Array<{
          path: string;
          entity_label: string;
          entity_type: string;
          kind: string;
          value: string;
          iface: string | null;
        }>;
      }>(scope, 'utm.show', { path: 'net' }, ['show', 'net']);
      const who = useState('');
      const [addr, setAddr] = who;
      const whoQ = useMutation({
        mutationFn: (a: string) =>
          callWidgetTool<{ objects?: Array<{ entity: string; kind: string; value: string }> }>(
            'utm.who',
            { addr: a },
            scope,
            { label: `utm.who ${a}` },
          ),
        onError: (e) => toast.error((e as Error).message),
      });
      const rows = q.data?.objects ?? [];
      return (
        <div className='px-3 py-2 space-y-2'>
          <div className='flex flex-wrap items-end gap-2'>
            <div className='min-w-[12rem]'>
              <Label>who</Label>
              <Input
                size='sm'
                className='font-mono'
                value={addr}
                onChange={(e) => setAddr(e.target.value)}
                placeholder='10.119.251.116'
              />
            </div>
            <Button
              size='sm'
              variant='secondary'
              disabled={!addr.trim() || whoQ.isPending}
              onClick={() => whoQ.mutate(addr.trim())}
            >
              who
            </Button>
            <Button
              size='sm'
              variant='secondary'
              onClick={() => {
                invalidateUtm(qc, scope);
                void q.refetch();
              }}
            >
              show net
            </Button>
          </div>
          {whoQ.data?.objects && (
            <p className='text-xs font-mono text-gray-400'>
              {whoQ.data.objects.length === 0
                ? t('registry.utm.nikdo', 'nikdo')
                : whoQ.data.objects.map((o) => `${o.entity} ${o.kind}=${o.value}`).join(' · ')}
            </p>
          )}
          <div className='overflow-x-auto'>
            <table className='w-full text-xs font-mono'>
              <thead className='text-gray-500 text-left'>
                <tr className='border-b border-gray-800'>
                  <th className='py-1 pr-2 font-medium'>entity</th>
                  <th className='py-1 pr-2 font-medium'>kind</th>
                  <th className='py-1 pr-2 font-medium'>value</th>
                  <th className='py-1 font-medium'>if</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className='py-3 text-gray-600'>
                      {t('registry.utm.zadnaNetVlastnostUtm', 'žádná net vlastnost — utm.adopt sem doplní adresy uzlu')}
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={`${r.entity_label}-${r.kind}-${r.value}`}
                    className='border-b border-gray-800/60 text-gray-200'
                  >
                    <td className='py-1.5 pr-2'>
                      {r.entity_label} <span className='text-gray-600'>{r.entity_type}</span>
                    </td>
                    <td className='pr-2'>{r.kind}</td>
                    <td className='pr-2'>{r.value}</td>
                    <td>{r.iface ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    },
  },
  {
    id: 'utm.interfaces',
    title: 'Interfaces',
    module: 'utm',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Cable,
    component: function Ifaces({ scope }: WidgetRenderProps) {
      const { t } = useTranslation();
      const q = useUtmQuery<{ interfaces?: Array<{ name: string; addr: string; state: string }> }>(
        scope,
        'utm.show',
        { path: 'interface' },
        ['show', 'interface'],
      );
      const rows = q.data?.interfaces ?? [];
      return (
        <div className='divide-y divide-gray-800/50'>
          {rows.length === 0 && (
            <div className='px-3 py-2 text-sm text-gray-600'>{t('registry.utm.zadneRozhrani', 'Žádné rozhraní')}</div>
          )}
          {rows.map((i) => (
            <div key={i.name} className='px-3 py-2 text-sm flex items-center justify-between gap-2'>
              <span className='font-mono text-gray-200'>{i.name}</span>
              <span className='text-xs text-gray-500'>
                {i.state} {i.addr}
              </span>
            </div>
          ))}
        </div>
      );
    },
  },
];
